/**
 * LA PALETTE — chercher dans les transferts, où que vous soyez.
 *
 * A small obsidian pill pinned to the bottom of the screen, exactly like Quick
 * Access's: out of the page's flow, so it follows you from the list of
 * handovers into one and on into a case, and finding something is one glance
 * down wherever you have scrolled. The bar that used to sit above the cards
 * was a filter for ONE screen; this is the section's way in.
 *
 * Two ways to use it, and the difference matters:
 *  · TYPE and it narrows what is on screen, instantly, with no network — the
 *    client, the service, who covers it, the tasks, the notes.
 *  · ASK (✦, or Enter when typing has found nothing) and the model READS those
 *    same items against the question — "Eva's CNF cases", "anything due before
 *    the 20th" — and answers with the ones that fit and one line each saying
 *    why. It ranks what you are looking at and nothing else, so every answer
 *    points at something you can open.
 *
 * ⌘K / Ctrl-K opens it from anywhere in the section; ↑↓ walk the results,
 * Enter opens, Esc closes.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { useKevoHandoff } from '@/components/KevoSpark';
import { askHandovers } from '@/integrations/handoverApi';
import { currentScope, subscribeScope, type PaletteItem } from './paletteStore';

const T = (lang: Lang, en: string, es: string, fr: string, pt?: string): string =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? (pt ?? en) : en;

const fold = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/* ── LA BARRA SE APARTA CUANDO OTRA COSA OCUPA ABAJO ──
   Fija abajo, tapaba lo que se abre encima de ella: la barra de selección
   múltiple del handover («40 cards selected… Unlink») quedaba detrás, y los
   paneles de filtros y de ajustes también (Gerónimo, 23 sep). Mientras uno
   de estos está en pantalla, la barra no se pinta; al cerrarlo, vuelve.
   Los pop-ups de un caso NO están aquí a propósito: la barra los sigue para
   buscar dentro. Un componente nuevo que deba apartarla solo tiene que
   llevar `data-covers-palette`. */
const COVERS = [
  '.hobulk', '.ho-bulkbar', '.mw-filters-scrim', '.mw-dd-scrim', '.tsk-picker-scrim', '.tsk-mobfilter-scrim',
  '.notes-filt-scrim', '.actes-cols-scrim', '.acv-pick-scrim', '.actes-ownpick-scrim', '[data-covers-palette]',
].join(', ');
function useCovered(): boolean {
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => { raf = 0; setCovered(!!document.querySelector(COVERS)); };
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(check); });
    mo.observe(document.body, { childList: true, subtree: true });
    check();
    return () => { mo.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return covered;
}

export function HandoverPalette({ lang }: { lang: Lang }) {
  const scope = useSyncExternalStore(subscribeScope, currentScope, currentScope);
  const covered = useCovered();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [asked, setAsked] = useState<{ id: string; why: string }[] | null>(null);
  /* A request, or the ✦, goes to Kevo (components/KevoSpark). */
  const kevo = useKevoHandoff(q, () => { setQ(''); setAsked(null); setOpen(false); input.current?.blur(); });
  const [asking, setAsking] = useState(false);
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  /* A new screen means a new question: carrying the previous one over would
     filter a list it was never about. */
  useEffect(() => { setQ(''); setAsked(null); setCursor(0); }, [scope?.owner]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  /* En modo discreto la caja no existe hasta abrirse: el foco va después. */
  useEffect(() => { if (open) input.current?.focus(); }, [open]);

  const items = scope?.items ?? [];
  const typed = q.trim();
  const hits: (PaletteItem & { why?: string })[] = asked
    ? asked.map((a) => {
        const it = items.find((i) => i.id === a.id);
        return it ? { ...it, why: a.why } : null;
      }).filter(Boolean) as (PaletteItem & { why?: string })[]
    : typed.length < 2
      ? []
      : items.filter((i) => fold(i.text).includes(fold(typed))).slice(0, 12);

  const ask = useCallback(async () => {
    if (typed.length < 2 || asking || !scope) return;
    setAsking(true);
    try {
      setAsked(await askHandovers(typed, items.map((i) => ({ id: i.id, text: i.text })), lang, scope.kind ?? 'handovers'));
      setCursor(0);
    } catch { setAsked([]); }
    setAsking(false);
  }, [typed, asking, scope, items, lang]);

  if (!scope) return null;
  if (scope.quiet && !open) return null;
  if (covered) return null;

  const pick = (id: string) => {
    scope.onPick(id);
    setQ(''); setAsked(null); setOpen(false);
    input.current?.blur();
  };

  const showPanel = open && (hits.length > 0 || typed.length >= 2 || !!scope.quiet);

  return (
    <>
      {showPanel && <div className="hpal-scrim" onClick={() => setOpen(false)} />}
      <div className={`hpal ${showPanel ? 'on' : ''} ${kevo.out}`}>
        {showPanel && (
          <div className="hpal-panel" role="listbox">
            {asked && (
              <div className="hpal-cap">
                ✦ {asked.length
                  ? T(lang, `${asked.length} read as an answer to your question`, `${asked.length} leídos como respuesta a tu pregunta`, `${asked.length} lus comme réponse à votre question`, `${asked.length} lidos como resposta`)
                  : T(lang, 'Nothing here answers that', 'Nada de aquí responde a eso', 'Rien ici n’y répond', 'Nada aqui responde a isso')}
              </div>
            )}
            {hits.map((h, i) => (
              <button key={h.id} type="button" role="option" aria-selected={i === cursor}
                className={`hpal-row ${i === cursor ? 'on' : ''}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(h.id)}>
                <span className="hpal-row-t">
                  <strong>{h.title}</strong>
                  {h.sub && <em>{h.sub}</em>}
                  {h.why && <em className="why">✦ {h.why}</em>}
                </span>
                {h.where && <span className="hpal-where">{h.where}</span>}
              </button>
            ))}
            {!hits.length && !asked && (
              <div className="hpal-none">
                {kevo.here ? T(lang,
                  'Nothing matches those words. Enter or ✦: ask Kevo.',
                  'Nada coincide con esas palabras. Enter o ✦: se lo preguntas a Kevo.',
                  'Rien ne correspond à ces mots. Entrée ou ✦ : demandez à Kevo.',
                  'Nada corresponde. Enter ou ✦: pergunte ao Kevo.') : T(lang,
                  'Nothing matches those words. Press ✦ and the model will read them for meaning.',
                  'Nada coincide con esas palabras. Pulsa ✦ y el modelo las lee por significado.',
                  'Rien ne correspond à ces mots. Pressez ✦ : le modèle les lit par le sens.',
                  'Nada corresponde. Carregue ✦ e o modelo lê por significado.')}
              </div>
            )}
          </div>
        )}

        <div className="hpal-bar">
          <KBIcon name="search" size={14} />
          <input
            ref={input}
            value={q}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQ(e.target.value); setAsked(null); setCursor(0); setOpen(true); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setOpen(false); input.current?.blur(); return; }
              if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, hits.length - 1)); return; }
              if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); return; }
              if (e.key === 'Enter') {
                e.preventDefault();
                if (kevo.enter()) return;
                if (hits[cursor]) pick(hits[cursor].id);
                else if (!kevo.here || !kevo.spark()) void ask();
              }
            }}
            placeholder={scope.hint}
            aria-label={scope.hint}
          />
          {q && <button className="hpal-x" onClick={() => { setQ(''); setAsked(null); }} aria-label={T(lang, 'Clear', 'Limpiar', 'Effacer', 'Limpar')}>×</button>}
          <button type="button" className={`hpal-ask ${asked ? 'on' : ''}`} disabled={kevo.here ? false : asking || typed.length < 2}
            onClick={() => { if (!kevo.here || !kevo.spark()) void ask(); }}
            title={kevo.here
              ? T(lang, 'Ask Kevo', 'Preguntar a Kevo', 'Demander à Kevo', 'Perguntar ao Kevo')
              : T(lang,
                'Ask instead of filter — the model reads what is on screen against your question',
                'Preguntar en vez de filtrar — el modelo lee lo que hay en pantalla con tu pregunta',
                'Demander au lieu de filtrer — le modèle lit ce qui est à l’écran avec votre question',
                'Perguntar em vez de filtrar')}>
            {asking ? '…' : '✦'}
          </button>
        </div>
      </div>
    </>
  );
}
